import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';

interface BaseResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
  status?: number;
}

export const supplierAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'GAME_CATALOG_API_BASE_URL',
});
attachAuthInterceptors(supplierAPI);

/** Payload item for create/update supplier */
export interface SupplierSupportedGameTopupTypePayload {
  game_topup_type_id: string;
  display_name: string;
  description?: string;
  link_format?: string;
}

/** Public catalog item: GET /game-topup-types/ */
export interface GameTopupTypeDto {
  id: string;
  name: string;
  requires_link: boolean;
}

export interface SupplierData {
  id: string;
  supplier_key: string;
  name: string;
  description: string;
  is_active: boolean;
  is_deleted: boolean;
  created_by_id: string;
  created_by_username: string;
  updated_by_id: string;
  updated_by_username: string;
  created_at: string;
  updated_at: string;
  supported_game_topup_types?: SupplierSupportedGameTopupTypePayload[];
}

export interface GetAllSuppliersRequest {
  page?: number;
  limit?: number;
  /** When true, request all rows; sends `all=true` and omits page/limit. */
  all?: boolean;
  search?: string;
  is_active?: boolean;
  include_deleted?: boolean;
}

export interface GetAllSuppliersResponse {
  success: boolean;
  data?: {
    data: SupplierData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export interface CreateSupplierRequest {
  name: string;
  supplier_key: string;
  description?: string;
  is_active: boolean;
  supported_game_topup_types: SupplierSupportedGameTopupTypePayload[];
}

export interface UpdateSupplierRequest {
  name: string;
  supplier_key: string;
  description?: string;
  is_active: boolean;
  supported_game_topup_types: SupplierSupportedGameTopupTypePayload[];
}

export interface UpdateSupplierStatusRequest {
  is_active: boolean;
}

export const getAllSuppliers = async (
  params: GetAllSuppliersRequest
): Promise<GetAllSuppliersResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      all,
      search,
      is_active,
      include_deleted,
    } = params;

    const queryParams = new URLSearchParams();

    if (all === true) {
      queryParams.append('all', 'true');
    } else {
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
    }

    if (search) queryParams.append('search', search);
    if (is_active !== undefined)
      queryParams.append('is_active', is_active.toString());
    if (include_deleted !== undefined)
      queryParams.append('include_deleted', include_deleted.toString());

    const response = await supplierAPI.get(
      `/admin/suppliers?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: response.data.message || 'Suppliers fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch suppliers',
    };
  }
};

export const getGameTopupTypes = async (): Promise<
  BaseResponse<GameTopupTypeDto[]>
> => {
  try {
    const response = await supplierAPI.get('/game-topup-types/');
    const list = response.data?.data ?? response.data ?? [];
    return {
      success: true,
      data: Array.isArray(list) ? list : [],
      message: response.data?.message || 'OK',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch game top-up types',
    };
  }
};

const isAbortLike = (error: unknown) => {
  const e = error as { code?: string; name?: string };
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError'
  );
};

export const getSupplierById = async (
  id: string,
  opts?: { signal?: AbortSignal }
): Promise<BaseResponse<SupplierData>> => {
  try {
    const response = await supplierAPI.get(`/admin/suppliers/${id}`, {
      skipGlobal500ErrorToast: true,
      signal: opts?.signal,
    });
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Supplier fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    return {
      success: false,
      status: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch supplier',
    };
  }
};

export const createSupplier = async (
  data: CreateSupplierRequest
): Promise<BaseResponse<SupplierData>> => {
  try {
    const response = await supplierAPI.post('/admin/suppliers', data);
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Supplier created successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create supplier',
      code: error.response?.data?.code,
      status: error.response?.status,
    };
  }
};

export const updateSupplier = async (
  id: string,
  data: UpdateSupplierRequest
): Promise<BaseResponse<SupplierData>> => {
  try {
    const response = await supplierAPI.put(`/admin/suppliers/${id}`, data);
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Supplier updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update supplier',
      code: error.response?.data?.code,
      status: error.response?.status,
    };
  }
};

export const deleteSupplier = async (
  id: string
): Promise<BaseResponse<SupplierData>> => {
  try {
    const response = await supplierAPI.delete(`/admin/suppliers/${id}/soft`);
    return {
      success: true,
      message: response.data.message || 'Supplier deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete supplier',
    };
  }
};

export const toggleSupplierActive = async (
  id: string,
  data: UpdateSupplierStatusRequest
): Promise<BaseResponse<SupplierData>> => {
  try {
    const response = await supplierAPI.patch(
      `/admin/suppliers/${id}/toggle-active`,
      data
    );
    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message || 'Supplier status updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update supplier status',
    };
  }
};

export interface SupplierOption {
  id: string;
  name: string;
}

export interface GetSupplierOptionsResponse {
  success: boolean;
  data?: SupplierOption[];
  message?: string;
}

export const getSupplierOptions = async (params?: {
  includeDeleted?: boolean;
  includeInactive?: boolean;
}): Promise<GetSupplierOptionsResponse> => {
  try {
    const queryParams: Record<string, boolean> = {};
    if (params?.includeDeleted) queryParams.include_deleted = true;
    if (params?.includeInactive) queryParams.include_inactive = true;

    const response = await supplierAPI.get('/suppliers/options', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });

    return {
      success: true,
      data: response.data,
      message: 'Supplier options fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch supplier options',
    };
  }
};
